import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import { extractErrorMessage } from '../api';
import { useToast } from '../components/Toast';
import { useProducts } from '../hooks/useProducts';
import { useCreateSale } from '../hooks/useSales';
import { useOfflineQueue } from '../hooks/useOfflineQueue';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'mobile_payment', label: 'Mobile Payment' },
];

const MobilePOS = () => {
  const { selectedStore } = useStore();
  const showToast = useToast();
  const { data: products = [], isLoading } = useProducts();
  const createSale = useCreateSale();
  const { enqueue, isOnline } = useOfflineQueue();

  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [discount, setDiscount] = useState(0);
  const [customerName, setCustomerName] = useState('');
  const [processing, setProcessing] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedStore?.currency || 'USD',
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const getStockBadgeClass = (product) => {
    if (product.quantity_in_stock === 0) return 'badge-danger';
    if (product.quantity_in_stock <= product.reorder_level) return 'badge-warning';
    return 'badge-success';
  };

  const filteredProducts = products.filter(p =>
    (p.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const addToCart = (product) => {
    if (product.quantity_in_stock === 0) {
      showToast('Product is out of stock', 'error');
      return;
    }
    setCart(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        if (existing.quantity >= product.quantity_in_stock) {
          showToast('Cannot exceed available stock', 'error');
          return prev;
        }
        return prev.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const updateQty = (productId, delta) => {
    setCart(prev =>
      prev
        .map(item =>
          item.product.id === productId
            ? { ...item, quantity: item.quantity + delta }
            : item
        )
        .filter(item => item.quantity > 0)
    );
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product.id !== productId));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.product.selling_price * item.quantity, 0);
  const total = Math.max(0, subtotal - parseFloat(discount || 0));

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    setProcessing(true);
    try {
      for (const item of cart) {
        const saleData = {
          store_id: selectedStore.id,
          product_id: item.product.id,
          quantity: item.quantity,
          customer_name: customerName,
          payment_method: paymentMethod,
          discount_amount: parseFloat(discount || 0) / cart.length,
        };
        if (isOnline) {
          await createSale.mutateAsync(saleData);
        } else {
          await enqueue(saleData);
        }
      }
      showToast(isOnline ? 'Sale completed!' : 'Sale queued (offline)', 'success');
      setCart([]);
      setCustomerName('');
      setDiscount(0);
    } catch (err) {
      showToast(extractErrorMessage(err, 'Sale failed'), 'error');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', minHeight: '80vh' }}>
      {/* Left: Product Grid */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="page-header" style={{ marginBottom: 16 }}>
          <div>
            <h1 className="page-title">POS — Point of Sale</h1>
            {!isOnline && (
              <span className="badge badge-warning" style={{ marginTop: 4 }}>Offline Mode</span>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <input
            type="text"
            className="form-input"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {isLoading ? (
          <div className="loading"><div className="spinner"></div><span>Loading products...</span></div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 12,
          }}>
            {filteredProducts.map(product => (
              <div
                key={product.id}
                onClick={() => addToCart(product)}
                style={{
                  cursor: product.quantity_in_stock === 0 ? 'not-allowed' : 'pointer',
                  borderRadius: 12,
                  padding: 16,
                  background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  transition: 'transform 120ms ease-out',
                  opacity: product.quantity_in_stock === 0 ? 0.5 : 1,
                }}
                onMouseEnter={e => { if (product.quantity_in_stock > 0) e.currentTarget.style.transform = 'scale(1.02)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 6, lineHeight: 1.3 }}>
                  {product.name}
                </div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--primary)', marginBottom: 8 }}>
                  {formatCurrency(product.selling_price)}
                </div>
                <span className={`badge ${getStockBadgeClass(product)}`} style={{ fontSize: '0.7rem' }}>
                  {product.quantity_in_stock === 0 ? 'Out' : `${product.quantity_in_stock} left`}
                </span>
              </div>
            ))}
            {filteredProducts.length === 0 && (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                No products found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right: Cart + Checkout */}
      <div style={{
        width: 300,
        flexShrink: 0,
        position: 'sticky',
        top: 24,
      }}>
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Cart ({cart.length} item{cart.length !== 1 ? 's' : ''})</h3>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {cart.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Tap a product to add it
              </div>
            ) : (
              <div>
                {cart.map(item => (
                  <div key={item.product.id} style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid var(--border-light)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.product.name}
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                        {formatCurrency(item.product.selling_price)} each
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => updateQty(item.product.id, -1)}
                        style={{ padding: '2px 8px', minWidth: 28 }}
                      >
                        -
                      </button>
                      <span style={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>{item.quantity}</span>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => updateQty(item.product.id, 1)}
                        style={{ padding: '2px 8px', minWidth: 28 }}
                        disabled={item.quantity >= item.product.quantity_in_stock}
                      >
                        +
                      </button>
                    </div>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => removeFromCart(item.product.id)}
                      style={{ padding: '2px 6px', color: 'var(--danger)' }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {cart.length > 0 && (
            <div style={{ padding: 16, borderTop: '1px solid var(--border-light)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '0.9rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>Subtotal</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label" style={{ fontSize: '0.8rem' }}>Discount</label>
                <input
                  type="number"
                  className="form-input"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  min="0"
                  step="0.01"
                  style={{ height: 34, fontSize: '0.9rem' }}
                />
              </div>

              <div style={{
                display: 'flex', justifyContent: 'space-between',
                fontWeight: 700, fontSize: '1.1rem', marginBottom: 16,
                paddingTop: 8, borderTop: '1px solid var(--border-light)',
              }}>
                <span>Total</span>
                <span style={{ color: 'var(--primary)' }}>{formatCurrency(total)}</span>
              </div>

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label" style={{ fontSize: '0.8rem' }}>Customer Name (optional)</label>
                <input
                  type="text"
                  className="form-input"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Walk-in customer"
                  style={{ height: 34, fontSize: '0.9rem' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label" style={{ fontSize: '0.8rem' }}>Payment Method</label>
                <select
                  className="form-select"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  style={{ height: 34, fontSize: '0.9rem' }}
                >
                  {PAYMENT_METHODS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              <button
                className="btn btn-primary"
                onClick={handleCheckout}
                disabled={processing || cart.length === 0}
                style={{ width: '100%' }}
              >
                {processing ? 'Processing...' : isOnline ? 'Complete Sale' : 'Queue Sale (Offline)'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobilePOS;
